import { useState, useEffect, memo, useMemo, useCallback } from 'react';
import { BrowserRouter, useNavigate, useLocation } from 'react-router-dom';
import { GameProvider, ShopProvider, EffectsProvider, SoundProvider } from '@/contexts';
import { LoggingProvider } from '@/contexts/LoggingContext';
import { AppRoutes } from '@/routes';
import SettingsMenuWrapper from '@/components/layout/SettingsMenuWrapper';
import PWAInstallPrompt from '@/components/layout/PWAInstallPrompt';
import { PerformanceOptimizer } from '@/components/layout/PerformanceOptimizer';
import PerformanceMonitor from '@/components/layout/PerformanceMonitor';
import { MobileNavBar, PixelIcon } from '@/components/ui';
import { LayoutGroup } from 'motion/react';
import { usePWA } from '@/hooks/usePWA';
import { ButtonSoundProvider } from '@/components/layout/ButtonSoundProvider';
import { getMobileNavItems } from '@/config/navigation';
import { ThemeManager } from '@/services/themeManager';
import { getUpgradeManager } from '@/game/upgrades';

// Import Tailwind CSS and custom styles
import '@/css/tailwind.css';
import '@/scss/global.scss';

// Mobile Navigation Component - Only shows on home screen
const MobileNavigation = memo(() => {
  const navigate = useNavigate();
  const location = useLocation();

  const mobileNavActions = useMemo(() => {
    const items = getMobileNavItems();
    return items.map(item => ({
      ...item,
      onClick: item.onClick || (() => {
        // Handle modal navigation
        if (item.id === 'profile') {
          navigate('/?modal=profile');
        } else if (item.id === 'settings') {
          navigate('/?modal=settings');
        } else if (item.id === 'shop') {
          navigate('/?modal=shop');
        } else if (item.id === 'levels') {
          navigate('/?modal=levels');
        } else if (item.path) {
          navigate(item.path);
        }
      })
    }));
  }, [navigate]);

  // Only show navigation on home screen or game screen
  if (location.pathname !== '/' && location.pathname !== '/game') {
    return null;
  }

  return <MobileNavBar actions={mobileNavActions} />;
});

const AppComponent = () => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showPerformanceMonitor, setShowPerformanceMonitor] = useState(false);
  const { isOnline } = usePWA();
  
  // Initialize theme on mount
  useEffect(() => {
    ThemeManager.initialize();
    // Initialize meta upgrades early so side-effects (like max lives) apply even before visiting /upgrades.
    getUpgradeManager();
    
    // Check for performance monitor in URL params or dev mode
    const urlParams = new URLSearchParams(window.location.search);
    const showPerfParam = urlParams.get('perf') === 'true';
    const isDev = import.meta.env.DEV;
    
    setShowPerformanceMonitor(showPerfParam || isDev);
    
    // Add keyboard shortcut for performance monitor (Ctrl+Shift+P)
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'P') {
        e.preventDefault();
        setShowPerformanceMonitor(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const openSettings = useCallback(() => {
    setIsSettingsOpen(true);
  }, []);

  const closeSettings = useCallback(() => {
    setIsSettingsOpen(false);
  }, []);

  return (
    <LoggingProvider>
      <BrowserRouter>
        <SoundProvider>
          <EffectsProvider>
            <GameProvider>
              <ShopProvider>
                <ButtonSoundProvider>
                  <LayoutGroup id="app-layout">
                    <main 
                    id="main" 
                    className="flex h-full w-full flex-col items-center justify-center text-color overflow-hidden"
                    style={{ height: '100vh' }}
                  >
                    <noscript className="text-center text-lg text-red-500">
                      Enable JavaScript to enjoy all the features of this app.
                    </noscript>

                  {/* Offline indicator */}
                  {!isOnline && (
                    <div className="offline-indicator">
                      <PixelIcon name="wifi" size={16} /> You're offline - some features may be limited
                    </div>
                  )}

                  {/* Performance optimizer with optional monitor */}
                  <PerformanceOptimizer showMonitor={showPerformanceMonitor} />
                  
                  {/* Performance Monitor */}
                  <PerformanceMonitor showDebugInfo={showPerformanceMonitor} autoOptimize={true} />
                  
                  {/* Router-based navigation */}
                  <AppRoutes onOpenSettings={openSettings} />

                  {/* Shared Settings Menu */}
                  <SettingsMenuWrapper
                    isOpen={isSettingsOpen}
                    onClose={closeSettings}
                    onRestart={() => {
                      console.log('Restart game');
                    }}
                    onGoHome={() => {
                      closeSettings();
                      window.location.href = '/';
                    }}
                    onToggleFullscreen={() => {
                      if (!document.fullscreenElement) {
                        document.documentElement.requestFullscreen();
                      } else {
                        document.exitFullscreen();
                      }
                    }}
                  />

                  {/* Global Mobile Navigation */}
                  <MobileNavigation />

                    {/* PWA Install Prompt */}
                    <PWAInstallPrompt />
                  </main>
                  </LayoutGroup>
                </ButtonSoundProvider>
              </ShopProvider>
            </GameProvider>
          </EffectsProvider>
        </SoundProvider>
      </BrowserRouter>
    </LoggingProvider>
  );
}

// Memoize App to prevent unnecessary re-renders
const App = memo(AppComponent);

export default App;
